/**
 * Shared web discovery layout for Home, Practices and Masters (CORE-1009).
 *
 * Hero artwork may remain full-bleed, while copy and catalogue content sit on
 * the same centered rail. Native screens keep their existing layout wiring;
 * these helpers are intentionally platform-agnostic so callers can opt in.
 */
export const DISCOVERY_SHELL_MAX_WIDTH = 1440;
export const DISCOVERY_GRID_GAP = 24;
export const DISCOVERY_GRID_HALF_GAP = DISCOVERY_GRID_GAP / 2;
export const DISCOVERY_COMPACT_HERO_BREAKPOINT = 1120;
export const DISCOVERY_DESKTOP_HEADER_BREAKPOINT = 1280;
export const DISCOVERY_MOBILE_BREAKPOINT = 768;
export const DISCOVERY_HERO_MOBILE_HEIGHT = 560;
export const DISCOVERY_HERO_TABLET_HEIGHT = 590;
export const DISCOVERY_HERO_DESKTOP_HEIGHT = 590;
export const DISCOVERY_HERO_MOBILE_COPY_OFFSET = 48;
export const DISCOVERY_HERO_COPY_OFFSET = 72;

export type DiscoveryHeroGeometry = {
  height: number;
  headerInset: 58 | 90;
  copyTop: number;
  gutter: number;
  compact: boolean;
};

export const discoveryPageGutter = (width: number): number => {
  if (width < 768) return 16;
  if (width < 1024) return 32;
  return 48;
};

export const discoveryCatalogColumnCount = (width: number): 1 | 2 | 3 | 4 => {
  if (width < 768) return 1;
  if (width < 1024) return 2;
  if (width < 1280) return 3;
  return 4;
};

export const discoveryCatalogItemWidth = (width: number): string => {
  const columns = discoveryCatalogColumnCount(width);
  if (columns === 1) return '100%';
  if (columns === 2) return '50%';
  if (columns === 3) return '33.333%';
  return '25%';
};

/**
 * One web hero geometry for Home, Practices and Masters (CORE-1009).
 *
 * The image always fills this fixed viewport instead of growing with copy or
 * the browser's aspect ratio. Page-specific artwork and actions remain slots;
 * the shared contract owns only the frame and copy rail.
 */
export const discoveryHeroGeometry = (width: number): DiscoveryHeroGeometry => {
  const compact = width < DISCOVERY_COMPACT_HERO_BREAKPOINT;
  const headerInset = width < DISCOVERY_DESKTOP_HEADER_BREAKPOINT ? 58 : 90;
  const height = width < DISCOVERY_MOBILE_BREAKPOINT
    ? DISCOVERY_HERO_MOBILE_HEIGHT
    : compact
      ? DISCOVERY_HERO_TABLET_HEIGHT
      : DISCOVERY_HERO_DESKTOP_HEIGHT;
  const copyOffset = width < DISCOVERY_MOBILE_BREAKPOINT
    ? DISCOVERY_HERO_MOBILE_COPY_OFFSET
    : DISCOVERY_HERO_COPY_OFFSET;

  return {
    height,
    headerInset,
    copyTop: headerInset + copyOffset,
    gutter: discoveryPageGutter(width),
    compact,
  };
};
